import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookMarked, Plus } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import {
  bookCopiesApi,
  booksApi,
  librariesApi,
  libraryLocationsApi,
  reservationsApi,
} from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { formatDate, orDash } from '@/utils/format';
import {
  Badge,
  ConditionBadge,
  CopyStatusBadge,
  Field,
  ReservationStatusBadge,
  Stat,
  authorsOf,
} from './shared';

/**
 * One title, and the physical copies of it.
 *
 * <p>The distinction the whole module rests on: a book is a catalogue record and
 * a copy is an object on a shelf. Two children cannot borrow the same copy, but
 * they can borrow the same book if the school bought two.
 *
 * <p>Withdrawing a title never deletes it. Every loan ever made points at this
 * record, and a school asked "who read this" three years later needs an answer.
 */
export function BookDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const managesCopies = can(PERMISSIONS.BOOK_COPY_MANAGE);
  const managesCatalogue = can(PERMISSIONS.CATALOGUE_MANAGE);
  const seesReservations = can(PERMISSIONS.RESERVATION_VIEW);

  const [accessioning, setAccessioning] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [prefix, setPrefix] = useState('');
  const [locationId, setLocationId] = useState('');
  const [price, setPrice] = useState('');

  const book = useQuery({
    queryKey: queryKeys.books.detail(id),
    queryFn: () => booksApi.get(id),
  });

  const copies = useQuery({
    queryKey: queryKeys.books.copies(id),
    queryFn: () => booksApi.copies(id),
  });

  const queue = useQuery({
    queryKey: queryKeys.reservations.queue(id),
    queryFn: () => reservationsApi.queue(id),
    enabled: seesReservations,
  });

  const libraries = useQuery({
    queryKey: queryKeys.library.list,
    queryFn: () => librariesApi.list(),
    enabled: accessioning,
  });

  const locations = useQuery({
    queryKey: queryKeys.library.locations(undefined),
    queryFn: () => libraryLocationsApi.list(),
    enabled: accessioning,
  });

  const accession = useMutation({
    mutationFn: () =>
      bookCopiesApi.create({
        bookId: id,
        libraryId: libraries.data?.[0]?.id ?? '',
        locationId: locationId === '' ? undefined : locationId,
        quantity: Number(quantity),
        accessionNumber: prefix.trim() === '' ? undefined : prefix.trim(),
        purchasePrice: price.trim() === '' ? undefined : Number(price),
      }),
    onSuccess: (created) => {
      toast.success(`${created.length} cop${created.length === 1 ? 'y' : 'ies'} accessioned`);
      setAccessioning(false);
      setQuantity('1');
      setPrefix('');
      setPrice('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (next: 'ACTIVE' | 'INACTIVE') => booksApi.setStatus(id, next),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'ACTIVE'
          ? 'Back in the catalogue'
          : 'Withdrawn — the borrowing history is untouched',
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (book.isError) {
    return <ErrorState error={book.error} onRetry={() => void book.refetch()} />;
  }
  if (book.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = book.data;

  return (
    <div>
      <Link
        to={ROUTES.catalogue}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Catalogue
      </Link>

      <PageHeader
        title={data.title}
        description={authorsOf(data.authors)}
        icon={<BookMarked className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex items-center gap-2">
            {data.status !== 'ACTIVE' && <Badge tone="neutral">withdrawn</Badge>}
            {managesCopies && (
              <Button onClick={() => setAccessioning(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add copies
              </Button>
            )}
            {managesCatalogue && (
              <Button
                variant="secondary"
                onClick={() => setStatus.mutate(data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
              >
                {data.status === 'ACTIVE' ? 'Withdraw' : 'Restore'}
              </Button>
            )}
          </span>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Copies" value={data.availability.totalCopies} />
        <Stat
          label="On the shelf"
          value={data.availability.availableCopies}
          tone={data.availability.availableCopies > 0 ? 'success' : 'warning'}
        />
        <Stat label="Out" value={data.availability.issuedCopies} />
        <Stat
          label="Waiting"
          value={data.availability.queueLength}
          tone={data.availability.queueLength > 0 ? 'warning' : 'neutral'}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Details</h2>
          <dl className="mt-2">
            <Field label="ISBN">
              <span className="font-mono">{orDash(data.isbn)}</span>
            </Field>
            <Field label="Subtitle">{orDash(data.subtitle)}</Field>
            <Field label="Category">{orDash(data.categoryName)}</Field>
            <Field label="Publisher">{orDash(data.publisherName)}</Field>
            <Field label="Year">{orDash(data.publicationYear)}</Field>
            <Field label="Edition">{orDash(data.edition)}</Field>
            <Field label="Language">{orDash(data.language)}</Field>
            <Field label="Pages">{orDash(data.pages)}</Field>
            <Field label="Subject">{orDash(data.subject)}</Field>
          </dl>
          {data.description !== undefined && (
            <p className="mt-3 border-t border-line pt-3 text-sm text-muted">{data.description}</p>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Copies</h2>
            <p className="mt-0.5 text-xs text-muted">
              Each one is a physical object with its own accession number.
            </p>
            {copies.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : (copies.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                Catalogued but not stocked — no copies have been accessioned yet.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-line">
                {(copies.data ?? []).map((copy) => (
                  <li key={copy.id} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-sm text-ink">
                        {copy.accessionNumber}
                      </span>
                      <span className="block text-[11px] text-subtle">
                        {orDash(copy.locationPath)}
                        {copy.currentBorrower !== undefined &&
                          ` · with ${copy.currentBorrower.memberName}, due ${formatDate(
                            copy.currentBorrower.dueDate,
                          )}`}
                      </span>
                    </span>
                    <ConditionBadge condition={copy.condition} />
                    <CopyStatusBadge status={copy.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {seesReservations && (queue.data ?? []).length > 0 && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Who is waiting
              </h2>
              <p className="mt-0.5 text-xs text-muted">
                In the order the server keeps them, which is the order they were told.
              </p>
              <ol className="mt-2 divide-y divide-line">
                {(queue.data ?? []).map((row) => (
                  <li key={row.id} className="flex items-center gap-3 py-2">
                    <span className="w-6 shrink-0 text-right font-bold tabular-nums text-ink">
                      {row.status === 'PENDING' ? row.queuePosition : '—'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{row.memberName}</span>
                      <span className="block text-[11px] text-subtle">
                        {row.status === 'READY'
                          ? `held on ${orDash(row.readyAccessionNumber)}, collect by ${
                              row.collectBy === undefined ? '—' : formatDate(row.collectBy)
                            }`
                          : `asked ${formatDate(row.requestedAt)}`}
                      </span>
                    </span>
                    <ReservationStatusBadge status={row.status} />
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>
      </div>

      <Modal
        open={accessioning}
        onClose={() => setAccessioning(false)}
        title="Accession copies"
        description="Creates the physical copies and numbers them in sequence."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAccessioning(false)}>
              Cancel
            </Button>
            <Button onClick={() => accession.mutate()} disabled={accession.isPending}>
              Accession them
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="How many"
            type="number"
            min={1}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            required
          />
          <TextField
            label="Starting accession number"
            value={prefix}
            onChange={(event) => setPrefix(event.target.value)}
            hint="Left blank, one is generated from the title. An existing number is skipped over rather than overwritten."
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Shelf
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Not shelved yet</option>
              {(locations.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.fullPath}
                </option>
              ))}
            </select>
          </label>
          <TextField
            label="What each cost"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            hint="Used to work out a replacement charge if a copy is lost."
          />
        </div>
      </Modal>
    </div>
  );
}
